import React, { useState, useMemo } from 'react';
import { Edit, BarChart3, MoreHorizontal, Plus } from 'lucide-react';
import { LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import {
  useReactTable, getCoreRowModel, getSortedRowModel, getFilteredRowModel, getPaginationRowModel, flexRender
} from '@tanstack/react-table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

const campaignsData = [
  { id: 1, name: "Pizza Palace - Local Launch", status: "running", budget: 1000, spend: 420, impressions: 15200, households: 4800, ctr: 2.8,
    dailyPerformance: [
      { date: '2024-11-01', impressions: 850, households: 280, spend: 25, day: 'Nov 1' },
      { date: '2024-11-02', impressions: 920, households: 310, spend: 28, day: 'Nov 2' },
      { date: '2024-11-03', impressions: 1100, households: 380, spend: 32, day: 'Nov 3' },
      { date: '2024-11-04', impressions: 980, households: 340, spend: 30, day: 'Nov 4' },
      { date: '2024-11-05', impressions: 1200, households: 420, spend: 35, day: 'Nov 5' },
      { date: '2024-11-06', impressions: 1080, households: 390, spend: 33, day: 'Nov 6' },
      { date: '2024-11-07', impressions: 1150, households: 400, spend: 34, day: 'Nov 7' },
    ]
  },
  { id: 2, name: "Fitness First - Holiday Promo", status: "running", budget: 2000, spend: 680, impressions: 23800, households: 7200, ctr: 2.1,
    dailyPerformance: [
      { date: '2024-11-01', impressions: 1200, households: 420, spend: 35, day: 'Nov 1' },
      { date: '2024-11-02', impressions: 1350, households: 480, spend: 42, day: 'Nov 2' },
      { date: '2024-11-03', impressions: 1180, households: 390, spend: 38, day: 'Nov 3' },
      { date: '2024-11-04', impressions: 1400, households: 520, spend: 45, day: 'Nov 4' },
      { date: '2024-11-05', impressions: 1320, households: 470, spend: 40, day: 'Nov 5' },
      { date: '2024-11-06', impressions: 1250, households: 430, spend: 38, day: 'Nov 6' },
      { date: '2024-11-07', impressions: 1180, households: 410, spend: 37, day: 'Nov 7' },
    ]
  },
  { id: 3, name: "Coffee Shop - Morning Rush", status: "paused", budget: 500, spend: 140, impressions: 6200, households: 2100, ctr: 1.9, dailyPerformance: [] },
  { id: 4, name: "Auto Dealer - Spring Sale", status: "draft", budget: 5000, spend: 0, impressions: 0, households: 0, ctr: 0, dailyPerformance: [] },
  { id: 5, name: "Business Week Campaign", status: "stopped", budget: 800, spend: 800, impressions: 18500, households: 5800, ctr: 2.4, dailyPerformance: [] }
];

const statusConfig = {
  running: { label: 'Running', className: 'bg-emerald-50 text-emerald-600 border-emerald-100' },
  paused: { label: 'Paused', className: 'bg-amber-50 text-amber-600 border-amber-100' },
  draft: { label: 'Draft', className: 'bg-slate-50 text-slate-500 border-slate-200' },
  stopped: { label: 'Stopped', className: 'bg-red-50 text-red-600 border-red-100' }
};

const PerformanceChart = ({ selectedCampaigns, metric, chartType }) => {
  const colors = ['#E8928D', '#7DCEC8', '#EDB95E', '#D4847E', '#A68AD9', '#5BBFD0', '#D98AAF'];

  const combinedData = useMemo(() => {
    if (!selectedCampaigns.length) return [];
    const allDates = new Set();
    selectedCampaigns.forEach(c => c.dailyPerformance?.forEach(d => allDates.add(d.date)));
    return Array.from(allDates).sort().map(date => {
      const point = { date, day: new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) };
      selectedCampaigns.forEach((c, i) => {
        const dayData = c.dailyPerformance?.find(d => d.date === date);
        point[`campaign_${i}`] = dayData ? dayData[metric] : 0;
      });
      return point;
    });
  }, [selectedCampaigns, metric]);

  if (!selectedCampaigns.length) {
    return <EmptyState title="No campaigns selected" description="Select one or more campaigns from the table below to view performance data" />;
  }

  if (!selectedCampaigns.some(c => c.dailyPerformance?.length > 0)) {
    return <EmptyState title="No performance data" description="No performance data available for selected campaigns" />;
  }

  const formatTooltip = (value, name) => {
    const idx = parseInt(name.split('_')[1]);
    const formatted = metric === 'spend' ? `$${value.toLocaleString()}` : value.toLocaleString();
    return [formatted, selectedCampaigns[idx]?.name || ''];
  };

  const ChartComponent = chartType === 'area' ? AreaChart : chartType === 'line' ? LineChart : BarChart;

  return (
    <ResponsiveContainer width="100%" height={450}>
      <ChartComponent data={combinedData}>
        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
        <XAxis dataKey="day" stroke="#8c8c8c" fontSize={12} />
        <YAxis stroke="#8c8c8c" fontSize={12} tickFormatter={v => metric === 'spend' ? `$${v}` : v.toLocaleString()} />
        <RechartsTooltip formatter={formatTooltip} contentStyle={{ backgroundColor: 'hsl(var(--card))', border: '1px solid hsl(var(--border))', borderRadius: '8px' }} />
        {selectedCampaigns.map((campaign, index) => {
          const color = colors[index % colors.length];
          if (chartType === 'area') return <Area key={campaign.id} type="monotone" dataKey={`campaign_${index}`} stackId="1" stroke={color} fill={color} fillOpacity={0.3} strokeWidth={2} />;
          if (chartType === 'line') return <Line key={campaign.id} type="monotone" dataKey={`campaign_${index}`} stroke={color} strokeWidth={3} dot={{ r: 4, fill: color }} activeDot={{ r: 6 }} />;
          return <Bar key={campaign.id} dataKey={`campaign_${index}`} fill={color} radius={[2, 2, 0, 0]} />;
        })}
      </ChartComponent>
    </ResponsiveContainer>
  );
};

const Campaigns = () => {
  const [selectedRows, setSelectedRows] = useState({});
  const [chartMetric, setChartMetric] = useState('impressions');
  const [chartType, setChartType] = useState('area');
  const [sorting, setSorting] = useState([]);

  const selectedCampaigns = useMemo(
    () => campaignsData.filter(c => selectedRows[c.id]),
    [selectedRows]
  );

  const columns = useMemo(() => [
    {
      id: 'select',
      header: ({ table }) => (
        <input type="checkbox"
          className="h-4 w-4 rounded border-border"
          checked={table.getIsAllPageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input type="checkbox"
          className="h-4 w-4 rounded border-border"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
      size: 40,
    },
    {
      accessorKey: 'name',
      header: 'Campaign Name',
      cell: ({ getValue }) => <span className="font-medium">{getValue()}</span>,
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const s = getValue();
        const cfg = statusConfig[s] || statusConfig.draft;
        return <Badge variant="outline" className={cfg.className}>{cfg.label}</Badge>;
      },
    },
    {
      accessorKey: 'budget',
      header: 'Budget',
      cell: ({ getValue }) => `$${getValue().toLocaleString()}`,
    },
    {
      accessorKey: 'spend',
      header: 'Spend',
      cell: ({ getValue }) => `$${getValue().toLocaleString()}`,
    },
    {
      accessorKey: 'impressions',
      header: 'Impressions',
      cell: ({ getValue }) => getValue().toLocaleString(),
    },
    {
      accessorKey: 'households',
      header: 'Households',
      cell: ({ getValue }) => getValue().toLocaleString(),
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: () => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon-xs" aria-label="Edit campaign"><Edit className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon-xs" aria-label="View report"><BarChart3 className="h-3.5 w-3.5" /></Button>
          <Button variant="ghost" size="icon-xs" aria-label="More actions"><MoreHorizontal className="h-3.5 w-3.5" /></Button>
        </div>
      ),
      size: 100,
    },
  ], []);

  const table = useReactTable({
    data: campaignsData,
    columns,
    state: { sorting, rowSelection: selectedRows },
    onSortingChange: setSorting,
    onRowSelectionChange: setSelectedRows,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => String(row.id),
    enableRowSelection: (row) => row.original.status === 'running' && row.original.dailyPerformance?.length > 0,
    initialState: { pagination: { pageSize: 15 } },
  });

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Campaigns</h1>
          <p className="text-muted-foreground mt-1">Manage your CTV advertising campaigns</p>
        </div>
        <Button size="lg"><Plus className="h-4 w-4 mr-2" />Create Campaign</Button>
      </div>

      <div className="space-y-6">
        {/* Performance Chart */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <div>
              <CardTitle className="text-base">Campaign Performance Comparison</CardTitle>
              {selectedCampaigns.length > 0 && (
                <p className="text-sm text-muted-foreground">{selectedCampaigns.length} campaign{selectedCampaigns.length !== 1 ? 's' : ''} selected</p>
              )}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Metric:</span>
                <Select value={chartMetric} onValueChange={setChartMetric}>
                  <SelectTrigger className="w-[130px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="impressions">Impressions</SelectItem>
                    <SelectItem value="households">Households</SelectItem>
                    <SelectItem value="spend">Spend</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium">Chart:</span>
                <Select value={chartType} onValueChange={setChartType}>
                  <SelectTrigger className="w-[100px]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="area">Area</SelectItem>
                    <SelectItem value="line">Line</SelectItem>
                    <SelectItem value="bar">Bar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="min-h-[500px]">
            <PerformanceChart selectedCampaigns={selectedCampaigns} metric={chartMetric} chartType={chartType} />
          </CardContent>
        </Card>

        {/* Selected campaigns legend */}
        {selectedCampaigns.length > 0 && (
          <Card>
            <CardContent className="py-3 px-6">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-medium">Selected Campaigns:</span>
                {selectedCampaigns.map((campaign, index) => {
                  const colors = ['bg-coral/70', 'bg-teal/70', 'bg-amber-400', 'bg-red-400', 'bg-violet-400', 'bg-cyan-400', 'bg-pink-400'];
                  return (
                    <Badge key={campaign.id} className={cn("text-white", colors[index % colors.length])}>
                      {campaign.name}
                    </Badge>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Campaigns Table */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
            <CardTitle className="text-base">Campaigns Overview</CardTitle>
            <p className="text-sm text-muted-foreground">Select campaigns to compare performance above</p>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border overflow-x-auto">
              <Table>
                <TableHeader>
                  {table.getHeaderGroups().map(headerGroup => (
                    <TableRow key={headerGroup.id}>
                      {headerGroup.headers.map(header => (
                        <TableHead
                          key={header.id}
                          className={cn(header.column.getCanSort() && "cursor-pointer select-none")}
                          onClick={header.column.getToggleSortingHandler()}
                        >
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getIsSorted() === 'asc' ? ' ↑' : header.column.getIsSorted() === 'desc' ? ' ↓' : ''}
                        </TableHead>
                      ))}
                    </TableRow>
                  ))}
                </TableHeader>
                <TableBody>
                  {table.getRowModel().rows.length ? (
                    table.getRowModel().rows.map(row => (
                      <TableRow key={row.id} data-state={row.getIsSelected() && "selected"}>
                        {row.getVisibleCells().map(cell => (
                          <TableCell key={cell.id}>
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={columns.length} className="h-24 text-center">
                        No campaigns found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                {table.getFilteredRowModel().rows.length} campaign{table.getFilteredRowModel().rows.length !== 1 ? 's' : ''}
              </p>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={() => table.previousPage()} disabled={!table.getCanPreviousPage()}>Previous</Button>
                <Button variant="outline" size="sm" onClick={() => table.nextPage()} disabled={!table.getCanNextPage()}>Next</Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Campaigns;
